import { put, takeLatest, retry } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';
import { saveAs } from 'file-saver';
import {
  Requests,
  ResolverResponse,
  Resources,
  Mutations,
} from '@erecruit/types';
import {
  getResourceApi,
  ShiftSearchCountRequestWithDataPref,
  ShiftSearchRequestWithDataPref,
} from '@erecruit/modules';
import { withStatusInfo } from '../../ActionStatus';
import {
  searchShifts as searchShiftsReducer,
  searchShiftsSuccess,
  searchShiftsError,
  cancelShiftSuccess,
  cancelShiftError,
  exportShiftsSuccess,
  exportShiftsFail,
} from './reducer';
import { shiftSearchActionBase } from './constants';
import { RETRY_DELAY_LENGTH, RETRY_MAX_TRIES } from '../../../constants';
import { CancelShiftData } from '../types';

function* searchShifts({
  payload,
}: PayloadAction<Resources.ShiftSearchParams>) {
  try {
    const responses = yield getResourceApi().getResources([
      new ShiftSearchRequestWithDataPref(payload),
      new ShiftSearchCountRequestWithDataPref({
        filter: payload.filter,
      }),
    ]) as Promise<ResolverResponse[]>;

    const response = responses[0] as ResolverResponse;

    if (response.success) {
      const data = responses[0].data as Resources.ShiftSearch[];
      const totalCount = responses[1].data[0].value as number;
      return yield put(searchShiftsSuccess({ data, totalCount }));
    }

    yield put(searchShiftsError(response.message));
  } catch (e) {
    console.error(e);
    yield put(searchShiftsError(e.message));
  }
}

function* cancelShiftErrorHandler(e: Error) {
  yield put(cancelShiftError(e.message));
}

function* cancelShift({ payload }: PayloadAction<CancelShiftData>) {
  const response = yield getResourceApi().createMutationRequest(
    new Requests.CancelShiftRequest(payload.params),
  );
  const shiftSearchParams: Resources.ShiftSearchParams = {
    filter: payload.filter,
  };

  yield put(cancelShiftSuccess(response));
  yield put(searchShiftsReducer(shiftSearchParams));
}

function* watchForRequestData() {
  yield takeLatest(`${shiftSearchActionBase}/searchShifts`, searchShifts);
}

function* watchForCancelShift() {
  yield takeLatest(
    `${shiftSearchActionBase}/cancelShift`,
    withStatusInfo({
      successGeneratorFn: cancelShift,
      errorGeneratorFn: cancelShiftErrorHandler,
      autocloseMs: 2000,
      getSuccessMessage: ({ payload: { shiftName, positionName } }) => ({
        id: messageIds.vms.shiftSearch.actions.successInfo,
        values: { shiftName, positionName },
      }),
      getLoadingMessage: ({ payload: { shiftName, positionName } }) => ({
        id: messageIds.vms.shiftSearch.actions.progressInfo,
        values: { shiftName, positionName },
      }),
      getHeader: ({ payload: { shiftName, positionName } }) => ({
        title: {
          id: messageIds.vms.shiftSearch.actions.actionTitle,
          values: { shiftName, positionName },
        },
      }),
    }),
  );
}

function* exportShifts({
  payload,
}: PayloadAction<
  Pick<Mutations.ExportShiftSearchParams, 'columns'> & {
    params: Mutations.ExportShiftSearchParams['shiftSearchParams'];
  }
>) {
  try {
    const { columns, params: shiftSearchParams } = payload;
    const [{ fileId }] = yield getResourceApi().createMutationRequest(
      new Requests.ExportShiftSearchRequest({ columns, shiftSearchParams }),
    );
    const blob = yield retry(RETRY_MAX_TRIES, RETRY_DELAY_LENGTH, () =>
      getResourceApi().getMedia(fileId, true),
    );

    saveAs(blob);

    yield put(exportShiftsSuccess({}));
  } catch (e) {
    yield put(exportShiftsFail(e.message));
  }
}

function* watchForExportShifts() {
  yield takeLatest('shiftSearch/exportShifts', exportShifts);
}

export default [
  watchForRequestData(),
  watchForCancelShift(),
  watchForExportShifts(),
];
